import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { cn } from 'lib/utils';

type AddUserHeaderProps = {
  title: string;
  description: string;
  /** The step shown, or null when the invite needs only one step. */
  step: 1 | 2 | null;
  onClose: () => void;
};

const stepDoneClassName = 'bg-grey-950 dark:bg-true-white';

/** Title, description and, for a two-step invite, which step this is. Figma 10827:7912 / 10827:9759. */
export const AddUserHeader = ({ title, description, step, onClose }: AddUserHeaderProps) => {
  const { t } = useTranslation();

  return (
    <>
      <button
        type="button"
        aria-label={t('CLOSE') || 'Close'}
        onClick={onClose}
        className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-card text-grey-500 hover:bg-grey-50 dark:text-grey-400 dark:hover:bg-muted"
      >
        <X className="size-4.5" strokeWidth={1.5} />
      </button>

      {step && (
        <div className="mb-3 flex gap-2" aria-hidden="true">
          <span className={cn('h-1 flex-1 rounded-xs', stepDoneClassName)} />
          <span
            className={cn('h-1 flex-1 rounded-xs', step === 2 ? stepDoneClassName : 'bg-grey-100 dark:bg-muted')}
          />
        </div>
      )}

      <DialogHeader className="mb-6 space-y-1 pr-8 text-left">
        {step && (
          <p className="text-xs font-medium leading-heading text-grey-500 dark:text-grey-400">
            {t('STEP_OF', { step, total: 2, defaultValue: 'Step {{step}} of {{total}}' })}
          </p>
        )}
        <DialogTitle className="text-lg font-medium leading-normal text-grey-950 dark:text-true-white">
          {title}
        </DialogTitle>
        <DialogDescription className="text-sm font-normal leading-body text-grey-500 dark:text-grey-400">
          {description}
        </DialogDescription>
      </DialogHeader>
    </>
  );
};
